import { observer } from 'mobx-react-lite'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import Styles from '../../BlogMetadata.module.scss'
import { BlogMetadataCompositionReturn } from '../../hooks/useBlogMetadataComposition'
import {
  BlogMetadataHeader,
  BlogMetadataImageSection,
  BlogMetadataCategoriesSection,
  WixIntegrationFields,
  SummaryField,
  TitleField,
  SlugField,
} from '..'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

/**
 * Wix-specific layout with custom field order
 * Order: Blog → Image → Writer → Excerpt → Categories → Tags → Title → Slug
 */
export const WixBlogMetadataLayout = observer(
  ({ integrationInfo, states, handlers, refs }: BlogMetadataCompositionReturn) => {
    const isSystemRedesignEnabled = useIsSystemRedesign()

    return (
      <div className={Styles.container} data-testid="blog-metadata-v2">
        <VisuallyHidden>
          <input
            aria-label="Upload featured image"
            ref={refs.fileInputRef}
            type="file"
            multiple={false}
            onChange={handlers.imageHandlers.handleFileInputChange}
            accept="image/*"
          />
        </VisuallyHidden>

        <BlogMetadataHeader blogIntegrationsCount={integrationInfo.blogIntegrationsCount} />

        {/* Wix Blog & Writer */}
        <WixIntegrationFields
          integrationState={states.integrationState}
          handlers={handlers.enhancedIntegrationHandlers}
          isPostPublished={integrationInfo.isPostPublished}
        />

        {/* Featured Image & Alt Text */}
        <BlogMetadataImageSection imagesState={states.imageState} handlers={handlers.imageHandlers} />

        {/* Excerpt (Summary) */}
        <SummaryField
          label="Excerpt"
          value={states.formState.summary}
          onChange={handlers.enhancedFormHandlers.handleSummaryChange}
        />

        {/* Categories & Tags - Wix limits: 10 categories, 30 tags per post */}
        <BlogMetadataCategoriesSection
          categoriesState={states.categoriesState}
          categoriesHandlers={handlers.enhancedCategoriesHandlers}
          tagsState={states.tagsState}
          tagsHandlers={handlers.enhancedTagsHandlers}
          maxCategories={10}
          maxTags={30}
          showCounters
        />

        {/* Title & Slug at the bottom */}
        <TitleField
          value={states.formState.title}
          onChange={handlers.enhancedFormHandlers.handleTitleChange}
          size={isSystemRedesignEnabled ? 'md' : 'lg'}
        />

        <SlugField
          value={states.formState.slug}
          onChange={handlers.enhancedFormHandlers.handleSlugChange}
          size={isSystemRedesignEnabled ? 'md' : 'lg'}
        />
      </div>
    )
  },
)
